import {
  DashboardResponse,
  LegacyBallDashboardData,
} from '../../../types/dashboard';
import { Users, Clock, Crown, Shield, UserCheck } from 'lucide-react';
import { StateIcon } from '../StateIcon';

interface LegacyBallPanelProps {
  data: DashboardResponse;
}

/** Map role names to Lucide icons */
function getRoleIcon(role: string) {
  switch (role) {
    case 'Admin': return <Crown size={22} color="#c9a84c" style={{ marginRight: 10, flexShrink: 0 }} />;
    case 'Organizer': return <Shield size={22} color="#c9a84c" style={{ marginRight: 10, flexShrink: 0 }} />;
    case 'Delegate': return <UserCheck size={22} color="#c9a84c" style={{ marginRight: 10, flexShrink: 0 }} />;
    default: return <Users size={22} color="#c9a84c" style={{ marginRight: 10, flexShrink: 0 }} />;
  }
}

const styles = {
  container: {
    padding: 'clamp(16px, 4vw, 48px)',
    overflow: 'hidden' as const,
    height: '100%',
    boxSizing: 'border-box' as const,
    background: '#0d1b12',
    color: '#ffffff',
    fontFamily: "'Inter', system-ui, sans-serif",
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 'clamp(12px, 3vw, 32px)',
  },
  heading: {
    fontSize: 'clamp(24px, 4vw, 48px)',
    color: '#c9a84c',
    margin: 0,
    fontWeight: 700,
  },
  statsRow: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: 'clamp(16px, 4vw, 48px)',
    alignItems: 'flex-end',
  },
  statBlock: {
    display: 'flex',
    flexDirection: 'column' as const,
  },
  statNumber: {
    fontSize: 'clamp(36px, 6vw, 72px)',
    fontWeight: 800,
    color: '#ffffff',
    margin: 0,
    lineHeight: 1.1,
  },
  statLabel: {
    fontSize: 'clamp(14px, 2vw, 24px)',
    color: '#e0e0e0',
    margin: '8px 0 0 0',
  },
  rateNumber: {
    fontSize: 'clamp(36px, 6vw, 72px)',
    fontWeight: 800,
    color: '#1a6b3c',
    margin: 0,
    lineHeight: 1.1,
  },
  columnsContainer: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: 'clamp(16px, 4vw, 48px)',
    flex: 1,
    overflow: 'hidden' as const,
  },
  leftColumn: {
    flex: 1,
    overflow: 'hidden' as const,
  },
  rightColumn: {
    flex: 1,
    overflow: 'hidden' as const,
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 'clamp(8px, 2vw, 24px)',
  },
  sectionTitle: {
    fontSize: 'clamp(16px, 2.5vw, 28px)',
    color: '#c9a84c',
    margin: '0 0 12px 0',
    fontWeight: 600,
  },
  listItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '8px 0',
    fontSize: 'clamp(14px, 2vw, 24px)',
    borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
  },
  listItemName: {
    color: '#ffffff',
  },
  listItemCount: {
    color: '#c9a84c',
    fontWeight: 700,
    fontSize: 'clamp(16px, 2.5vw, 28px)',
  },
  listItemChapter: {
    color: '#e0e0e0',
    fontSize: 'clamp(12px, 1.7vw, 20px)',
  },
  listItemTime: {
    color: '#c9a84c',
    fontSize: 'clamp(12px, 1.7vw, 20px)',
  },
};

/**
 * Legacy Ball area panel — shows combined registration and check-in stats,
 * chapter breakdown with dual counts, role breakdown, and recent check-ins feed.
 */
export function LegacyBallPanel({ data }: LegacyBallPanelProps) {
  const ballData = data as LegacyBallDashboardData;

  const formatTime = (isoString: string) => {
    try {
      return new Date(isoString).toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return '';
    }
  };

  return (
    <div style={styles.container}>
      <h1 style={styles.heading}>
        <Users size={42} style={{ marginRight: 16, verticalAlign: 'middle' }} />
        Legacy Ball
      </h1>

      {/* Stat Row */}
      <div style={styles.statsRow}>
        <div style={styles.statBlock}>
          <p style={styles.statNumber}>{ballData.totalRegistrations}</p>
          <p style={styles.statLabel}>Total Registered</p>
        </div>
        <div style={styles.statBlock}>
          <p style={styles.statNumber}>{ballData.totalCheckedIn}</p>
          <p style={styles.statLabel}>Checked In</p>
        </div>
        <div style={styles.statBlock}>
          <p style={styles.rateNumber}>{ballData.checkInRate}%</p>
          <p style={styles.statLabel}>Check-In Rate</p>
        </div>
      </div>

      {/* Two-Column Layout */}
      <div style={styles.columnsContainer}>
        {/* Left Column: Chapter Breakdown */}
        <div style={styles.leftColumn}>
          <h2 style={styles.sectionTitle}>Chapter Breakdown</h2>
          {ballData.registrationsByChapter.map((item) => (
            <div key={item.chapter} style={styles.listItem}>
              <span style={{ ...styles.listItemName, display: 'flex', alignItems: 'center' }}>
                <StateIcon state={item.chapter} size={64} color="#c9a84c" style={{ marginRight: 10 }} />
                {item.chapter}
              </span>
              <span style={styles.listItemCount}>
                {item.registrationCount} reg / {item.checkedInCount} in
              </span>
            </div>
          ))}
        </div>

        {/* Right Column */}
        <div style={styles.rightColumn}>
          {/* Role Breakdown */}
          <div>
            <h2 style={styles.sectionTitle}>Role Breakdown</h2>
            {ballData.registrationsByRole.map((item) => (
              <div key={item.role} style={styles.listItem}>
                <span style={{ ...styles.listItemName, display: 'flex', alignItems: 'center' }}>
                  {getRoleIcon(item.role)}
                  {item.role}
                </span>
                <span style={styles.listItemCount}>{item.count}</span>
              </div>
            ))}
          </div>

          {/* Recent Check-Ins */}
          <div style={{ flex: 1, overflow: 'hidden' as const }}>
            <h2 style={styles.sectionTitle}>Recent Check-Ins</h2>
            {ballData.recentCheckIns.map((item, index) => (
              <div key={index} style={styles.listItem}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <UserCheck size={20} color="#1a6b3c" style={{ marginRight: 10, flexShrink: 0 }} />
                  <span style={styles.listItemName}>{item.name}</span>
                  <span style={styles.listItemChapter}> — {item.chapter}</span>
                </div>
                <span style={{ ...styles.listItemTime, display: 'flex', alignItems: 'center' }}>
                  <Clock size={16} color="#c9a84c" style={{ marginRight: 6 }} />
                  {formatTime(item.checkedInAt)}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
